import React, { useState, useEffect } from 'react';
import { Zap, PlusCircle } from 'lucide-react';

const HallucinationsSection = ({ isDarkMode }) => {
  const [hallucinations, setHallucinations] = useState([]);
  const [newHallucination, setNewHallucination] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [isRegex, setIsRegex] = useState(false);
  const [isWildcard, setIsWildcard] = useState(false);
  const [error, setError] = useState(null);
  const [edgeServerEndpoint] = useState(process.env.REACT_APP_EDGE_SERVER_ENDPOINT || "http://localhost:4000");

  // Fetch existing hallucinations on component mount
  useEffect(() => {
    const fetchHallucinations = async () => {
      try {
        const response = await fetch(`${edgeServerEndpoint}/hallucinations`);
        if (!response.ok) {
          throw new Error('Failed to fetch hallucinations');
        }
        const data = await response.json();
        setHallucinations(data);
      } catch (err) {
        console.error('Error fetching hallucinations:', err);
        setError('Failed to load hallucinations');
      }
    };
    fetchHallucinations();
  }, []);

  const handleAddHallucination = async () => {
    if (!newHallucination.trim()) return;

    const hallucinationObj = {
      text: newHallucination,
      type: [
        ...(isRegex ? ['regex'] : []),
        ...(isWildcard ? ['wildcard'] : []),
      ],
      created_by: 'user', // Replace with actual user if available
    };

System: try {
      const response = await fetch(`${edgeServerEndpoint}/hallucinations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(hallucinationObj),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add hallucination');
      }

      const newHallucinationFromServer = await response.json();
      setHallucinations([...hallucinations, newHallucinationFromServer]);
      setNewHallucination('');
      setIsRegex(false);
      setIsWildcard(false);
      setError(null);
    } catch (err) {
      console.error('Error adding hallucination:', err);
      setError(err.message);
    }
  };

  const handleRemoveHallucination = async (hallucinationToRemove) => {
    try {
      const response = await fetch(`${edgeServerEndpoint}/hallucinations/${hallucinationToRemove.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete hallucination');
      }

      setHallucinations(
        hallucinations.filter((h) => h.id !== hallucinationToRemove.id)
      );
      setError(null);
    } catch (err) {
      console.error('Error deleting hallucination:', err);
      setError(err.message);
    }
  };

  return (
    <div
      className={`relative overflow-hidden rounded-2xl shadow-lg transition-all duration-300 ${
        isDarkMode
          ? 'bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700'
          : 'bg-gradient-to-br from-white to-slate-50 border border-slate-200'
      }`}
    >
      {/* Background decoration elements */}
      <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full opacity-10 bg-purple-500 blur-xl"></div>
      <div className="absolute bottom-10 -left-10 w-32 h-32 rounded-full opacity-10 bg-pink-500 blur-xl"></div>

      <div className="p-8 relative z-10">
        {/* Header */}
        <div className="flex items-center gap-5 mb-8">
          <div
            className={`p-3 rounded-xl shadow-md ${
              isDarkMode
                ? 'bg-gradient-to-br from-purple-600 to-purple-700'
                : 'bg-gradient-to-br from-purple-500 to-purple-600'
            }`}
          >
            <Zap size={22} className="text-white" />
          </div>
          <div>
            <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
              Hallucinations
            </h2>
            <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              Add patterns to detect potential hallucinations in transcriptions
            </p>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-100 text-red-700">
            {error}
          </div>
        )}

        {/* Input Area */}
        <div
          className={`flex flex-col gap-3 p-1 rounded-xl transition-all duration-300 ${
            isFocused ? (isDarkMode ? 'bg-slate-700/50' : 'bg-purple-50/50') : ''
          }`}
        >
          <div className="flex gap-3">
            <input
              type="text"
              value={newHallucination}
              onChange={(e) => setNewHallucination(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && newHallucination.trim() && handleAddHallucination()}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              placeholder="Enter new hallucination pattern"
              className={`flex-1 p-3 rounded-lg transition-all duration-300 ${
                isDarkMode
                  ? 'bg-slate-800 border border-slate-700 text-slate-200 placeholder-slate-500 focus:border-purple-500/70'
                  : 'bg-white border border-slate-200 text-slate-800 placeholder-slate-400 focus:border-purple-400/70'
              } focus:outline-none`}
            />
            <button
              onClick={handleAddHallucination}
              disabled={!newHallucination.trim()}
              aria-label="Add hallucination"
              className={`px-5 py-3 rounded-lg flex items-center gap-2 font-medium transition-all duration-300 ${
                newHallucination.trim()
                  ? isDarkMode
                    ? 'bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white shadow-lg'
                    : 'bg-gradient-to-r from-purple-500 to-purple-400 hover:from-purple-600 hover:to-purple-500 text-white shadow-md'
                  : isDarkMode
                    ? 'bg-slate-800 text-slate-600 cursor-not-allowed'
                    : 'bg-slate-100 text-slate-400 cursor-not-allowed'
              }`}
            >
              <PlusCircle size={18} />
              <span>Add</span>
            </button>
          </div>

          {/* Regex and Wildcard Options */}
          <div className="flex gap-4 px-3">
            <label className={`flex items-center gap-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
              <input
                type="checkbox"
                checked={isRegex}
                onChange={(e) => setIsRegex(e.target.checked)}
                className="rounded border-slate-300 text-purple-500 focus:ring-purple-500"
              />
              Regex
            </label>
            <label className={`flex items-center gap-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
              <input
                type="checkbox"
                checked={isWildcard}
                onChange={(e) => setIsWildcard(e.target.checked)}
                className="rounded border-slate-300 text-purple-500 focus:ring-purple-500"
              />
              Wildcard
            </label>
          </div>
        </div>

        {/* Hallucinations Display */}
        <div className="mt-6">
          <h3 className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            {hallucinations.length > 0
              ? `${hallucinations.length} Hallucination Patterns`
              : 'No hallucination patterns added yet'}
          </h3>

          {hallucinations.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {hallucinations.map((hallucination, index) => (
                <div
                  key={`${hallucination.text}-${index}`}
                  className={`group px-4 py-2 rounded-full flex items-center gap-2 transition-all duration-300 ${
                    isDarkMode
                      ? 'bg-slate-800/80 border border-slate-700 hover:border-purple-500/50'
                      : 'bg-white border border-slate-200 hover:border-purple-400/50 hover:shadow-sm'
                  }`}
                >
                  <span className={`font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    {hallucination.text}
                    {hallucination.type.includes('regex') && (
                      <span className="ml-1 text-xs text-purple-500">[Regex]</span>
                    )}
                    {hallucination.type.includes('wildcard') && (
                      <span className="ml-1 text-xs text-purple-500">[Wildcard]</span>
                    )}
                  </span>
                  <button
                    onClick={() => handleRemoveHallucination(hallucination)}
                    className={`flex items-center justify-center w-5 h-5 rounded-full ${
                      isDarkMode
                        ? 'text-slate-500 hover:text-white hover:bg-red-500'
                        : 'text-slate-400 hover:text-white hover:bg-red-500'
                    } transition-colors duration-300`}
                    aria-label={`Remove hallucination ${hallucination.text}`}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div
              className={`text-center py-10 rounded-xl ${
                isDarkMode ? 'bg-slate-800/50 border border-slate-700' : 'bg-slate-50 border border-dashed border-slate-200'
              }`}
            >
              <p className={`text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                Add hallucination patterns to detect potential errors in your transcriptions
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HallucinationsSection;